import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import {
  TrendingUp, TrendingDown, DollarSign, Target, Phone, MessageSquare,
  Flame, Trophy, ArrowUpRight, AlertCircle
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip, Cell,
} from "recharts";

export default function ROI() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/campaigns/metrics");
        setData(data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="p-8 text-sm text-zinc-500 font-mono">Loading…</div>;

  const totals = data.reduce(
    (acc, d) => ({
      revenue: acc.revenue + d.metrics.revenue_usd,
      cost: acc.cost + d.metrics.cost_usd,
      profit: acc.profit + d.metrics.profit_usd,
      leads: acc.leads + d.metrics.total_leads,
      won: acc.won + d.metrics.pipeline.won,
    }),
    { revenue: 0, cost: 0, profit: 0, leads: 0, won: 0 }
  );
  const totalRoi = totals.cost > 0 ? (totals.profit / totals.cost) * 100 : 0;

  const roiChart = data.map((d) => ({
    name: d.campaign.name.slice(0, 14),
    roi: d.metrics.roi_pct,
    cost: d.metrics.cost_usd,
    revenue: d.metrics.revenue_usd,
  }));

  return (
    <div className="p-8 max-w-[1400px]" data-testid="roi-page">
      <div className="border-b border-zinc-200 pb-6 mb-8 flex items-end justify-between">
        <div>
          <div className="overline mb-2">/ campaign roi · capital allocation</div>
          <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight">Return per Campaign</h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
            Which niche × city combo prints money? Ranked by ROI. Higher = ship more leads there.
          </p>
        </div>
        <button
          onClick={() => navigate("/campaign")}
          data-testid="roi-cta-campaign"
          className="bg-[#002FA7] hover:bg-[#00227A] text-white px-4 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-2"
        >
          New Campaign <ArrowUpRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Global totals */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8" data-testid="roi-totals">
        <TotalCard
          icon={<DollarSign className="w-4 h-4" />} accent="#10B981"
          label="Revenue" value={`$${totals.revenue.toLocaleString(undefined,{maximumFractionDigits:0})}`}
        />
        <TotalCard
          icon={<TrendingDown className="w-4 h-4" />} accent="#EF4444"
          label="Spend" value={`$${totals.cost.toLocaleString(undefined,{maximumFractionDigits:2})}`}
        />
        <TotalCard
          icon={<TrendingUp className="w-4 h-4" />} accent={totals.profit >= 0 ? "#002FA7" : "#EF4444"}
          label="Profit" value={`${totals.profit >= 0 ? "$" : "-$"}${Math.abs(totals.profit).toLocaleString(undefined,{maximumFractionDigits:2})}`}
        />
        <TotalCard
          icon={<Target className="w-4 h-4" />} accent="#0A0A0A"
          label="Total ROI" value={`${totalRoi >= 0 ? "+" : ""}${totalRoi.toFixed(0)}%`}
        />
        <TotalCard
          icon={<Trophy className="w-4 h-4" />} accent="#F59E0B"
          label="Deals Won" value={String(totals.won)}
        />
      </div>

      {data.length === 0 ? (
        <div className="border border-dashed border-zinc-300 bg-zinc-50 py-16 text-center" data-testid="roi-empty">
          <AlertCircle className="w-8 h-8 mx-auto text-zinc-400 mb-3" />
          <div className="font-heading text-lg font-bold">No campaigns yet</div>
          <div className="text-sm text-zinc-500 mt-1">Create a campaign first to see ROI breakdowns.</div>
        </div>
      ) : (
        <>
          {/* A/B test hint: if two campaigns share niche, highlight it */}
          {abTestHint(data) && (
            <div className="mb-6 px-4 py-3 bg-[#002FA7] text-white text-xs flex items-center gap-3" data-testid="ab-hint">
              <span className="overline" style={{ color: "white" }}>/ a/b test detected</span>
              <span className="font-mono">{abTestHint(data)}</span>
            </div>
          )}
          {/* Chart */}
          <div className="border border-zinc-200 bg-white mb-8">
            <div className="px-5 py-4 border-b border-zinc-200">
              <div className="overline">/ roi by campaign</div>
              <h2 className="font-heading text-lg font-bold mt-1">Best ROI = Where to Double Down</h2>
            </div>
            <div className="p-5 h-[280px]" data-testid="roi-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={roiChart}>
                  <CartesianGrid stroke="#E4E4E7" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#A1A1AA" />
                  <YAxis tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} stroke="#A1A1AA" tickFormatter={(v) => `${v}%`} />
                  <Tooltip
                    cursor={{ fill: "rgba(0,47,167,0.06)" }}
                    contentStyle={{ borderRadius: 0, border: "1px solid #E4E4E7", fontSize: 12 }}
                    formatter={(v) => [`${v.toFixed(1)}%`, "ROI"]}
                  />
                  <Bar dataKey="roi">
                    {roiChart.map((d, i) => (
                      <Cell key={i} fill={d.roi >= 0 ? "#002FA7" : "#EF4444"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Per-campaign breakdown */}
          <div className="space-y-4" data-testid="roi-list">
            {data.map((row) => (
              <CampaignRow key={row.campaign.id} row={row} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function TotalCard({ icon, label, value, accent }) {
  return (
    <div className="border border-zinc-200 bg-white p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="overline">{label}</div>
        <div style={{ color: accent }}>{icon}</div>
      </div>
      <div className="font-heading text-2xl font-black tracking-tight">{value}</div>
    </div>
  );
}

function CampaignRow({ row }) {
  const m = row.metrics;
  const profitable = m.profit_usd >= 0;
  return (
    <div className="border border-zinc-200 bg-white" data-testid={`roi-row-${row.campaign.id}`}>
      <div className="grid grid-cols-12 gap-0">
        {/* Left: campaign info */}
        <div className="col-span-12 lg:col-span-4 p-5 border-b lg:border-b-0 lg:border-r border-zinc-200">
          <div className="overline mb-2">/ campaign</div>
          <div className="font-heading text-xl font-bold leading-tight">{row.campaign.name}</div>
          <div className="text-xs text-zinc-500 font-mono mt-2">
            {row.campaign.target_niche} · {row.campaign.target_city || "any"}
          </div>
          <div className="mt-4 pt-4 border-t border-zinc-100">
            <div className="overline mb-1">Deal Size</div>
            <div className="font-mono text-sm font-bold">${row.campaign.deal_size_usd?.toLocaleString() || "2,500"}</div>
          </div>
        </div>

        {/* Middle: ROI / financial */}
        <div className="col-span-12 lg:col-span-4 p-5 border-b lg:border-b-0 lg:border-r border-zinc-200" style={{ background: profitable ? "rgba(16,185,129,0.04)" : "rgba(239,68,68,0.04)" }}>
          <div className="overline mb-2">/ financial</div>
          <div className="flex items-baseline gap-3 mb-4">
            <div className="font-heading text-4xl font-black" style={{ color: profitable ? "#10B981" : "#EF4444" }}>
              {m.roi_pct >= 0 ? "+" : ""}{m.roi_pct.toFixed(0)}%
            </div>
            <div className="overline">ROI</div>
          </div>
          <div className="grid grid-cols-3 gap-3 text-xs">
            <Stat label="Revenue" value={`$${m.revenue_usd.toLocaleString()}`} />
            <Stat label="Spend" value={`$${m.cost_usd.toFixed(2)}`} />
            <Stat label="Profit" value={`${m.profit_usd >= 0 ? "$" : "-$"}${Math.abs(m.profit_usd).toFixed(2)}`} highlight={profitable ? "#10B981" : "#EF4444"} />
          </div>
        </div>

        {/* Right: funnel */}
        <div className="col-span-12 lg:col-span-4 p-5">
          <div className="overline mb-3">/ funnel</div>
          <div className="grid grid-cols-3 gap-3 text-xs mb-3">
            <Stat label="Leads" value={m.total_leads} icon={<Target className="w-3 h-3" />} />
            <Stat label="Hot" value={m.hot_leads} icon={<Flame className="w-3 h-3" />} />
            <Stat label="Won" value={m.pipeline.won} icon={<Trophy className="w-3 h-3" />} />
          </div>
          <div className="grid grid-cols-3 gap-3 text-xs">
            <Stat label="SMS" value={m.sms_sent} icon={<MessageSquare className="w-3 h-3" />} />
            <Stat label="Calls" value={m.calls_made} icon={<Phone className="w-3 h-3" />} />
            <Stat label="CAC" value={m.cac_usd != null ? `$${m.cac_usd.toFixed(0)}` : "—"} />
          </div>
        </div>
      </div>

      {/* Recommendation strip */}
      <div className="border-t border-zinc-200 px-5 py-3 bg-zinc-50 flex items-center gap-3">
        <div className="overline">/ ai recommendation</div>
        <div className="text-xs text-zinc-700 flex-1">
          {recommendation(m)}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, icon, highlight }) {
  return (
    <div>
      <div className="overline flex items-center gap-1 mb-1">{icon} {label}</div>
      <div className="font-mono text-sm font-bold" style={highlight ? { color: highlight } : {}}>{value}</div>
    </div>
  );
}

function recommendation(m) {
  if (m.total_leads === 0) return "No leads yet — scrape some to begin tracking ROI.";
  if (m.pipeline.won > 0 && m.roi_pct > 200) return `🚀 Top performer. Scale this niche/city — every $1 returns $${(m.roi_pct / 100 + 1).toFixed(1)}.`;
  if (m.hot_leads > 0 && m.calls_made === 0) return `🔥 You have ${m.hot_leads} Hot leads not called yet. Start calling.`;
  if (m.contact_rate_pct < 30 && m.total_leads > 5) return `⚠️ Only ${m.contact_rate_pct.toFixed(0)}% contacted. Enable auto-sequences to scale touches.`;
  if (m.pipeline.demo > 0) return `${m.pipeline.demo} demos in flight — close them to lift ROI.`;
  if (m.contact_rate_pct >= 30 && m.pipeline.won === 0) return "Touches happening but no closes — refine your call script or value prop in Campaign settings.";
  return "Keep feeding leads to this campaign and re-check ROI weekly.";
}

function abTestHint(data) {
  // Find pairs of campaigns sharing the same niche
  const byNiche = {};
  data.forEach((d) => {
    const k = `${d.campaign.target_niche}|${d.campaign.target_city || ""}`.toLowerCase();
    (byNiche[k] ||= []).push(d);
  });
  for (const [, arr] of Object.entries(byNiche)) {
    if (arr.length >= 2) {
      const sorted = arr.slice().sort((a, b) => b.metrics.roi_pct - a.metrics.roi_pct);
      const winner = sorted[0];
      const loser = sorted[sorted.length - 1];
      if (winner.metrics.total_leads === 0 && loser.metrics.total_leads === 0) continue;
      const lift = winner.metrics.roi_pct - loser.metrics.roi_pct;
      return `"${winner.campaign.name}" beats "${loser.campaign.name}" by ${lift.toFixed(0)}% ROI on ${winner.campaign.target_niche}`;
    }
  }
  return null;
}
